import React from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import logo from '../assets/logo.png';
import { useAuth } from '../hooks/useAuth';
import { useAuthModal } from '../components/Layout';
import '../styles/Home.css';

const CHROME_STORE_URL =
  'https://chromewebstore.google.com/detail/shop-with-tailor/pkbjbfpllpngngkbidkmagfdohfmhipa';

function Home() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const openAuth = useAuthModal();

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleShopForOthersCta = () => {
    if (isAuthenticated) {
      navigate('/people');
    } else {
      openAuth('signup');
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <img src={logo} alt="Tailor logo" className="hero-logo" />
          <br />
          <Logo size="xl" clickable={false} />
          <p className="hero-tagline">Your AI-powered fit companion</p>
          <p className="hero-subtitle">
            Get accurate size recommendations. Reduce returns. Shop sustainably.
          </p>
          <div className="hero-ctas">
            <button
              className="btn btn-primary btn-lg"
              onClick={() => scrollToSection('extension')}
            >
              Get Started
            </button>
            <button
              className="btn btn-outline btn-lg"
              onClick={() => scrollToSection('problem')}
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="section problem-section" id="problem">
        <div className="section-inner">
          <h2 className="section-heading">The Problem with Online Sizing</h2>
          <p className="section-intro">
            The fashion industry faces a massive sustainability challenge. Inaccurate sizing leads
            to excessive returns, wasted resources, and a significant carbon footprint.
          </p>
          <div className="stat-cards">
            <div className="stat-card">
              <span className="stat-number">30%</span>
              <span className="stat-label">of online clothing is returned</span>
              <span className="stat-detail">Size-related issues are the #1 reason</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">24M</span>
              <span className="stat-label">metric tons of CO2 annually</span>
              <span className="stat-detail">Generated by clothing returns worldwide</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">$7B+</span>
              <span className="stat-label">lost to size-related returns</span>
              <span className="stat-detail">A cost borne by retailers and the planet</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section how-section">
        <div className="section-inner">
          <h2 className="section-heading">How It Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
              </div>
              <h3 className="step-title">1. Enter Your Measurements</h3>
              <p className="step-desc">
                Input your body measurements once. We store them securely and use them across all stores.
              </p>
            </div>
            <div className="step">
              <div className="step-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <h3 className="step-title">2. Browse & Shop</h3>
              <p className="step-desc">
                Shop as usual. Our Chrome extension automatically detects clothing pages and size guides.
              </p>
            </div>
            <div className="step">
              <div className="step-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="step-title">3. Get Instant Recommendations</h3>
              <p className="step-desc">
                Receive your recommended size instantly, powered by AI analysis of each store's size guide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Shop for Others */}
      <section className="section shop-others-section" id="shop-for-others">
        <div className="section-inner">
          <div className="shop-others-badge">New</div>
          <h2 className="section-heading">Buy clothes for anyone. Nail the size every time.</h2>
          <p className="section-intro">
            A gift for your partner. School clothes for your kids. A birthday jumper for your dad.
            You shouldn't need to memorise anyone's chest measurement — or ruin the surprise by
            asking. Connect once, and Tailor shows you <strong>their</strong> size on any store,
            right when you're shopping.
          </p>

          <div className="shop-others-cards">
            <div className="shop-others-card">
              <div className="shop-others-card-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 12 20 22 4 22 4 12" />
                  <rect x="2" y="7" width="20" height="5" />
                  <line x1="12" y1="22" x2="12" y2="7" />
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                </svg>
              </div>
              <h3>Gifts that actually fit</h3>
              <p>
                No more guessing between M and L, and no more "keep the receipt just in case."
                Get the size right first time, without spoiling the surprise.
              </p>
            </div>
            <div className="shop-others-card">
              <div className="shop-others-card-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>One account per family isn't enough</h3>
              <p>
                Everyone keeps their own measurements up to date on their own account.
                You just pick whose size you want to see while you shop.
              </p>
            </div>
            <div className="shop-others-card">
              <div className="shop-others-card-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h3>Private by design</h3>
              <p>
                Connections are mutual — both people accept. And you only ever see someone's
                recommended <em>size</em>, never their actual measurements.
              </p>
            </div>
          </div>

          <div className="shop-others-how">
            <div className="shop-others-step">
              <span className="shop-others-step-num">1</span>
              <p>Add someone by their Tailor username</p>
            </div>
            <div className="shop-others-arrow">→</div>
            <div className="shop-others-step">
              <span className="shop-others-step-num">2</span>
              <p>They accept your request</p>
            </div>
            <div className="shop-others-arrow">→</div>
            <div className="shop-others-step">
              <span className="shop-others-step-num">3</span>
              <p>Pick their name in the size popup while you shop</p>
            </div>
          </div>

          <div className="shop-others-cta">
            <button className="btn btn-primary btn-lg" onClick={handleShopForOthersCta}>
              {isAuthenticated ? 'Add Your People' : 'Sign Up & Start Connecting'}
            </button>
          </div>
        </div>
      </section>

      {/* Download Extension */}
      <section className="section extension-section" id="extension">
        <div className="section-inner extension-content">
          <div className="extension-badge extension-badge-live">Now Live</div>
          <h2 className="section-heading">Get the Chrome Extension</h2>
          <p className="extension-desc">
            Add Tailor to Chrome and it automatically detects clothing pages and recommends your
            size — no extra steps needed. Install it free from the Chrome Web Store.
          </p>

          <a
            className="webstore-card"
            href={CHROME_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="webstore-card-chrome">
              <span className="webstore-dot webstore-dot-red" />
              <span className="webstore-dot webstore-dot-yellow" />
              <span className="webstore-dot webstore-dot-green" />
              <span className="webstore-card-url">chromewebstore.google.com</span>
            </div>
            <div className="webstore-card-body">
              <img src={logo} alt="Tailor" className="webstore-card-logo" />
              <div className="webstore-card-info">
                <span className="webstore-card-name">Shop with Tailor</span>
                <span className="webstore-card-meta">Chrome Web Store · Shopping</span>
                <div className="webstore-card-rating">
                  <span className="webstore-stars" aria-hidden="true">★★★★★</span>
                  <span className="webstore-card-free">Free</span>
                </div>
              </div>
              <span className="webstore-card-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add to Chrome
              </span>
            </div>
          </a>

          <a
            className="btn btn-primary btn-lg extension-store-btn"
            href={CHROME_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on the Chrome Web Store
          </a>
        </div>
      </section>

      {/* Open Source / Self-hosting */}
      <section className="section opensource-section" id="open-source">
        <div className="section-inner opensource-box">
          <h2 className="section-heading">Free &amp; Open Source</h2>
          <p className="section-intro">
            Tailor's entire stack — extension, backend, AI service, and this website — is open
            source under the MIT license. Run your own instance and your measurements never leave
            your server.
          </p>
          <code className="opensource-command">
            git clone https://github.com/DickonC/shop-with-tailor.git
          </code>
          <p className="opensource-hint">
            Then follow{' '}
            <a
              href="https://github.com/DickonC/shop-with-tailor/blob/main/DEPLOYMENT.md"
              target="_blank"
              rel="noopener noreferrer"
            >
              DEPLOYMENT.md
            </a>{' '}
            to go from a fresh server to a live deployment.
          </p>
          <a
            className="btn btn-lg btn-github"
            href="https://github.com/DickonC/shop-with-tailor"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
            </svg>
            View on GitHub
          </a>
        </div>
      </section>

      {/* Transparency */}
      <section className="section transparency-section">
        <div className="section-inner">
          <h2 className="section-heading">Built with Transparency</h2>
          <div className="transparency-cards">
            <div className="transparency-card">
              <h3>Free to Use</h3>
              <p>TAILOR is completely free. No hidden costs, no premium tiers, no subscriptions.</p>
            </div>
            <div className="transparency-card">
              <h3>Research-Funded</h3>
              <p>This project is funded by a small research grant to cover running costs. We don't monetise your data.</p>
            </div>
            <div className="transparency-card">
              <h3>No Data Selling</h3>
              <p>Your measurements are yours. We never sell, share, or monetise your personal data.</p>
            </div>
            <div className="transparency-card">
              <h3>Always Improving</h3>
              <p>TAILOR is constantly being developed and improved, with new features and better fit accuracy rolling out over time.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
